import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Icon, IconButton, Tooltip, CircularProgress, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Download, UploadFile } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import MDSelect from 'components/MDSelect';
import { get, postNoResponse, postFile } from 'utils/WebService';
import { errorAlert, successAlert, formatarParaMoeda } from 'utils/Functions';
import { MDTextFieldCalendar } from 'components/MDTextFiedCalendar';

export default function Pledge({ id }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingUpload, setIsLoadingUpload] = useState(false);
    const [isDownload, setIsDownload] = useState(false);
    const [pledgeList, setPledgeList] = useState([]);
    
    // Using the built-in formatCurrency for masking
    const formatCurrency = (val) => val === undefined || val === null ? 'R$ 0,00' : 'R$ ' + Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const initialFormState = {
        descricao: "",
        dataPenhora: new Date().toISOString().split('T')[0],
        idFasePenhora: null,
        idTipoBemPenhora: null,
        matricula: "",
        modelo: "",
        placa: "",
        nomeArquivo: "",
        base64File: "",
        tipo: "PENHORA",
        idProcesso: id
    };

    const [formData, setFormData] = useState(initialFormState);
    const [fasePenhoraOptions, setFasePenhoraOptions] = useState([]);
    const [tipoBemPenhoraOptions, setTipoBemPenhoraOptions] = useState([]);

    const [filterData, setFilterData] = useState("");
    const [filterFase, setFilterFase] = useState(null);

    useEffect(() => {
        get("/fase-penhora/list-all")
            .then(res => setFasePenhoraOptions(res.list || []))
            .catch(() => setFasePenhoraOptions([]));
        
        get("/tipo-bem-penhora/list-all")
            .then(res => setTipoBemPenhoraOptions(res.list || []))
            .catch(() => setTipoBemPenhoraOptions([]));
    }, []);

    useEffect(() => {
        if (id) {
            handleList();
        }
    }, [id]);

    const handleList = () => {
        setIsLoading(true);
        get(`/caso-processual-penhora/list-all/${id}`)
            .then(({ list }) => {
                setPledgeList(list || []);
            })
            .catch(() => setPledgeList([]))
            .finally(() => setIsLoading(false));
    };

    const handleSave = () => {
        if (!formData.descricao || !formData.valor) {
            errorAlert("Preencha a descrição e o valor.");
            return;
        }

        const tipoBem = formData.idTipoBemPenhora?.descricao?.toUpperCase() || "";
        const isImovel = tipoBem.includes("IMÓVE") || tipoBem.includes("IMOVEL");
        const isVeiculo = tipoBem.includes("VEÍCUL") || tipoBem.includes("VEICUL");

        if (isImovel && !formData.matricula) {
            errorAlert("Preencha a Matrícula do imóvel.");
            return;
        }

        if (isVeiculo && (!formData.modelo || !formData.placa)) {
            errorAlert("Preencha o Modelo e a Placa do veículo.");
            return;
        }

        setIsSaving(true);
        const endpoint = editingId ? "/caso-processual-penhora/save" : "/caso-processual-penhora/init";
        
        const payload = {
            ...formData,
            id: editingId ? editingId : null,
            idFasePenhora: formData.idFasePenhora ? formData.idFasePenhora.id : null,
            idTipoBemPenhora: formData.idTipoBemPenhora ? formData.idTipoBemPenhora.id : null,
        };

        postNoResponse(endpoint, payload)
            .then(() => {
                successAlert(editingId ? "Penhora atualizada!" : "Penhora registrada!");
                handleCancel();
                handleList();
            })
            .catch((err) => errorAlert(err.message || "Erro ao salvar penhora"))
            .finally(() => setIsSaving(false));
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormData({
            descricao: item.descricao,
            valor: item.valor,
            dataPenhora: item.dataPenhora,
            matricula: item.matricula || "",
            modelo: item.modelo || "",
            placa: item.placa || "",
            nomeArquivo: item.nomeArquivo || "",
            idFasePenhora: item.idFasePenhora ? { id: item.idFasePenhora, descricao: item.descricaoFasePenhora } : null,
            idTipoBemPenhora: item.idTipoBemPenhora ? { id: item.idTipoBemPenhora, descricao: item.descricaoTipoBemPenhora } : null,
            tipo: item.tipo,
            idProcesso: id
        });
        setShowForm(true);
    };

    const handleDelete = (pledgeId) => {
        postNoResponse(`/caso-processual-penhora/delete/${pledgeId}`)
            .then(() => {
                successAlert("Penhora excluída!");
                handleList();
            })
            .catch(() => errorAlert("Erro ao excluir"));
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData(initialFormState);
    };

    function handleDownloadFile(idPenhora, nomeArquivo) {
        setIsDownload(true);
        postFile(`/caso-processual-penhora/download/${idPenhora}`, {}, `${nomeArquivo}`)
            .then(() => setIsDownload(false))
            .catch(() => setIsDownload(false));
    }

    const onFileChange = async ({ target }) => {
        setIsLoadingUpload(true);
        const file = target.files[0];
        if (!file) {
            setIsLoadingUpload(false);
            return;
        }

        try {
            const base64String = await fileToBase64(file);
            setFormData(prev => ({ ...prev, base64File: base64String, nomeArquivo: file.name }));
            setIsLoadingUpload(false);
        } catch (error) {
            console.error("Erro ao ler o arquivo: ", error);
            errorAlert("Erro ao ler o arquivo!");
            setIsLoadingUpload(false);
        }
    };

    function fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (err) => reject(err);
        });
    }

    const filteredPledgeList = useMemo(() => {
        return pledgeList.filter(item => {
            const matchData = filterData ? item.dataPenhora === filterData : true;
            const matchFase = filterFase ? item.idFasePenhora === filterFase.id : true;
            return matchData && matchFase;
        });
    }, [pledgeList, filterData, filterFase]);

    return (
        <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6">Penhoras do Processo</MDTypography>
                {!showForm && (
                    <MDButton variant="gradient" color="info" onClick={() => setShowForm(true)}>
                        <Icon sx={{ mr: 1 }}>add</Icon> Nova Penhora
                    </MDButton>
                )}
            </MDBox>

            {(!showForm && !isLoading) && (
                <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">Filtrar por Data</MDTypography>
                            </MDBox>
                            <MDTextFieldCalendar
                                type="date"
                                fullWidth
                                value={filterData}
                                onChange={(e) => setFilterData(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">Filtrar por Fase</MDTypography>
                            </MDBox>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.descricao}
                                placeholder="Todas as fases..."
                                value={filterFase}
                                options={fasePenhoraOptions}
                                onChange={selected => setFilterFase(selected)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5} display="flex" justifyContent="flex-end">
                            <MDButton size="medium" variant="contained" color="info" onClick={() => { setFilterData(""); setFilterFase(null); }}>
                                Limpar Filtros
                            </MDButton>
                        </Grid>
                    </Grid>
                </Card>
            )}

            {showForm ? (
                <MDBox component="form" pb={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">
                                    Data da Penhora
                                </MDTypography>
                            </MDBox>
                            <MDTextFieldCalendar
                                type="date"
                                fullWidth
                                value={formData.dataPenhora}
                                onChange={(e) => setFormData({ ...formData, dataPenhora: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">
                                    Fase da Penhora
                                </MDTypography>
                            </MDBox>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.descricao}
                                placeholder="Selecione..."
                                value={formData.idFasePenhora}
                                options={fasePenhoraOptions}
                                onChange={selected => setFormData({ ...formData, idFasePenhora: selected })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">
                                    Tipo do Bem
                                </MDTypography>
                            </MDBox>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.descricao}
                                placeholder="Selecione..."
                                value={formData.idTipoBemPenhora}
                                options={tipoBemPenhoraOptions}
                                onChange={selected => setFormData({ ...formData, idTipoBemPenhora: selected })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">
                                    Valor Avaliado
                                </MDTypography>
                            </MDBox>
                            <MDInput
                                type="text"
                                fullWidth
                                placeholder="R$ 0,00"
                                value={formatCurrency(formData.valor || "0")}
                                onChange={({ target }) => {
                                    let numeric = target.value.replace(/\D/g, "");
                                    if (!numeric) numeric = "0";
                                    setFormData({ ...formData, valor: (parseInt(numeric, 10) / 100).toFixed(2) });
                                }}
                            />
                        </Grid>

                        {(formData.idTipoBemPenhora?.descricao?.toUpperCase().includes("IMÓVE") || formData.idTipoBemPenhora?.descricao?.toUpperCase().includes("IMOVEL")) && (
                            <Grid item xs={12} sm={4}>
                                <MDBox mb={1} ml={0.5}>
                                    <MDTypography component="label" variant="caption" fontWeight="bold">Matrícula *</MDTypography>
                                </MDBox>
                                <MDInput fullWidth value={formData.matricula} onChange={e => setFormData({ ...formData, matricula: e.target.value })} />
                            </Grid>
                        )}

                        {(formData.idTipoBemPenhora?.descricao?.toUpperCase().includes("VEÍCUL") || formData.idTipoBemPenhora?.descricao?.toUpperCase().includes("VEICUL")) && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <MDBox mb={1} ml={0.5}>
                                        <MDTypography component="label" variant="caption" fontWeight="bold">Modelo *</MDTypography>
                                    </MDBox>
                                    <MDInput fullWidth value={formData.modelo} onChange={e => setFormData({ ...formData, modelo: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDBox mb={1} ml={0.5}>
                                        <MDTypography component="label" variant="caption" fontWeight="bold">Placa *</MDTypography>
                                    </MDBox>
                                    <MDInput fullWidth value={formData.placa} onChange={e => setFormData({ ...formData, placa: e.target.value })} />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={12}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">
                                    Descrição do Bem
                                </MDTypography>
                            </MDBox>
                            <MDInput
                                fullWidth
                                placeholder="Ex: Veículo Placa ABC-1234"
                                value={formData.descricao}
                                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <MDBox mb={1} ml={0.5}>
                                <MDTypography component="label" variant="caption" fontWeight="bold">
                                    Anexo da Penhora
                                </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <MDButton
                                    variant="contained"
                                    component="label"
                                    startIcon={!isLoadingUpload && <UploadFile />}
                                    color="info"
                                    endIcon={isLoadingUpload ? <CircularProgress color="white" size={20} sx={{ ml: 0, mr: 0 }} /> : null}
                                >
                                    {!isLoadingUpload && "Upload"}
                                    <input
                                        type="file"
                                        onChange={onFileChange}
                                        hidden
                                    />
                                </MDButton>

                                {(editingId && formData.nomeArquivo) && (
                                    <MDButton
                                        variant="outlined"
                                        startIcon={!isDownload && <Download />}
                                        color="info"
                                        onClick={() => handleDownloadFile(editingId, formData.nomeArquivo)}
                                        endIcon={isDownload ? <CircularProgress color="white" size={20} sx={{ ml: 0, mr: 0 }} /> : null}
                                    >
                                        {!isDownload && "Baixar"}
                                    </MDButton>
                                )}

                                {formData.nomeArquivo && (
                                    <MDTypography variant="caption" color="text">
                                        Arquivo anexado: <b>{formData.nomeArquivo}</b>
                                    </MDTypography>
                                )}
                            </MDBox>
                        </Grid>
                    </Grid>

                    <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                        <MDButton variant="outlined" color="error" onClick={handleCancel}>
                            Cancelar
                        </MDButton>
                        <MDButton variant="gradient" color="info" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? <CircularProgress size={24} color="inherit" /> : (editingId ? "Salvar Alterações" : "Confirmar Penhora")}
                        </MDButton>
                    </MDBox>
                </MDBox>
            ) : (
                isLoading ? (
                    <MDBox display="flex" justifyContent="center" p={5}>
                        <CircularProgress color="info" />
                    </MDBox>
                ) : (
                    <MDBox>
                        {filteredPledgeList.length > 0 ? (
                        <TableContainer component={Paper} elevation={1} sx={{ mt: 2, borderRadius: 2 }}>
                            <Table>
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        <TableCell><MDTypography variant="caption" fontWeight="bold" color="secondary">DATA</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" fontWeight="bold" color="secondary">FASE</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" fontWeight="bold" color="secondary">TIPO DO BEM</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" fontWeight="bold" color="secondary">DADOS DO BEM</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" fontWeight="bold" color="secondary">DESCRIÇÃO</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" fontWeight="bold" color="secondary">VALOR</MDTypography></TableCell>
                                        <TableCell align="right"><MDTypography variant="caption" fontWeight="bold" color="secondary">AÇÕES</MDTypography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPledgeList.map((item, index) => {
                                        const isImovel = item.descricaoTipoBemPenhora?.toUpperCase().includes("IMÓVE") || item.descricaoTipoBemPenhora?.toUpperCase().includes("IMOVEL");
                                        const isVeiculo = item.descricaoTipoBemPenhora?.toUpperCase().includes("VEÍCUL") || item.descricaoTipoBemPenhora?.toUpperCase().includes("VEICUL");
                                        
                                        return (
                                            <TableRow key={item.id || index} hover>
                                                <TableCell>
                                                    <MDTypography variant="button" display="block">
                                                        {item.dataPenhora ? item.dataPenhora.split('-').reverse().join('/') : "-"}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <MDTypography variant="button" display="block">{item.descricaoFasePenhora || "-"}</MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <MDTypography variant="button" display="block">{item.descricaoTipoBemPenhora || "-"}</MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    {isImovel && item.matricula ? (
                                                        <MDTypography variant="button" display="block" color="text">Matrícula: {item.matricula}</MDTypography>
                                                    ) : isVeiculo && (item.modelo || item.placa) ? (
                                                        <MDTypography variant="button" display="block" color="text">
                                                            {item.modelo ? `Mod: ${item.modelo}` : ""} {item.placa ? `Placa: ${item.placa}` : ""}
                                                        </MDTypography>
                                                    ) : (
                                                        <MDTypography variant="button" display="block" color="text">-</MDTypography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <MDTypography variant="button" display="block" sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.descricao}>
                                                        {item.descricao}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <MDTypography variant="button" display="block" color="info" fontWeight="medium">
                                                        {formatarParaMoeda(item.valor)}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    {item.nomeArquivo && (
                                                        <Tooltip title={`Baixar Anexo: ${item.nomeArquivo}`}>
                                                            <IconButton color="success" onClick={() => handleDownloadFile(item.id, item.nomeArquivo)} size="small" sx={{ p: 0.5 }}>
                                                                <Icon fontSize="small">download</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Editar">
                                                        <IconButton color="info" onClick={() => handleEdit(item)} size="small" sx={{ p: 0.5 }}>
                                                            <Icon fontSize="small">edit</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Excluir">
                                                        <IconButton color="error" onClick={() => handleDelete(item.id)} size="small" sx={{ p: 0.5 }}>
                                                            <Icon fontSize="small">delete</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        ) : (
                            <MDBox textAlign="center" py={5}>
                                <MDTypography variant="h6" color="secondary" opacity={0.6}>Nenhuma penhora encontrada.</MDTypography>
                            </MDBox>
                        )}
                    </MDBox>
                )
            )}
        </MDBox>
    );
}
